import React, { useState } from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { SharedLayout } from '../components/layout/SharedLayout';

const PageWrapper = styled.div`
  min-height: 100vh;
  background-color: ${theme.colors.neutral.white};
`;

const HeroSection = styled.div`
  background-color: ${theme.colors.primary.dark};
  color: ${theme.colors.neutral.white};
  padding: ${theme.spacing['3xl']} ${theme.spacing.xl};
  text-align: center;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${theme.spacing.xl};

  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: 0 ${theme.spacing.md};
  }
`;

const Headline = styled.h1`
  font-size: ${theme.typography.sizes['4xl']};
  margin-bottom: ${theme.spacing.lg};
  font-weight: ${theme.typography.fontWeights.bold};

  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: ${theme.typography.sizes['3xl']};
  }
`;

const Badge = styled.div`
  background-color: ${theme.colors.seafoam};
  color: ${theme.colors.primary.dark};
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.full};
  display: inline-block;
  margin-bottom: ${theme.spacing.xl};
  font-weight: ${theme.typography.fontWeights.semibold};
`;

const FormSection = styled.section`
  padding: ${theme.spacing['3xl']} 0;
  background-color: ${theme.colors.neutral.lightGrey};
`;

const FormContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
  background-color: ${theme.colors.neutral.white};
  padding: ${theme.spacing['2xl']};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.lg};
`;

const FormTitle = styled.h2`
  font-size: ${theme.typography.sizes['2xl']};
  margin-bottom: ${theme.spacing.lg};
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-weight: ${theme.typography.fontWeights.medium};
  margin-bottom: ${theme.spacing.sm};
`;

const Input = styled.input`
  padding: ${theme.spacing.md};
  border: 1px solid ${theme.colors.neutral.grey};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.md};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.blue};
  }
`;

const Select = styled.select`
  padding: ${theme.spacing.md};
  border: 1px solid ${theme.colors.neutral.grey};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.md};
  background-color: ${theme.colors.neutral.white};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.blue};
  }
`;

const SubmitButton = styled.button`
  background-color: ${theme.colors.primary.blue};
  color: ${theme.colors.neutral.white};
  padding: ${theme.spacing.lg};
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.fontWeights.semibold};
  cursor: pointer;
  transition: ${theme.transitions.base};

  &:hover {
    background-color: ${theme.colors.primary.dark};
  }
`;

const BenefitsSection = styled.section`
  padding: ${theme.spacing['3xl']} 0;
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${theme.spacing.xl};
  margin-top: ${theme.spacing['2xl']};
`;

const BenefitCard = styled.div`
  text-align: center;
  padding: ${theme.spacing.xl};
`;

const BenefitIcon = styled.div`
  font-size: 3rem;
  margin-bottom: ${theme.spacing.md};
`;

const BenefitTitle = styled.h3`
  font-size: ${theme.typography.sizes.xl};
  margin-bottom: ${theme.spacing.md};
  font-weight: ${theme.typography.fontWeights.semibold};
`;

const CTASection = styled.section`
  background-color: ${theme.colors.primary.beige};
  padding: ${theme.spacing['3xl']} 0;
  text-align: center;
`;

const PhoneNumber = styled.a`
  font-size: ${theme.typography.sizes['3xl']};
  color: ${theme.colors.primary.blue};
  text-decoration: none;
  font-weight: ${theme.typography.fontWeights.bold};
  
  &:hover {
    text-decoration: underline;
  }
`;

export const APELanding: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    annuityCompany: '',
    annuityType: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission here
    console.log('Form submitted:', formData);
  };

  return (
    <SharedLayout>
      <PageWrapper>
      <HeroSection>
        <Container>
          <Badge>As Featured on National TV</Badge>
          <Headline>Know What You Own™</Headline>
        </Container>
      </HeroSection>

      <FormSection>
        <Container>
          <FormContainer>
            <FormTitle>Get Your Free APE Report</FormTitle>
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label htmlFor="name">Full Name</Label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="annuityCompany">Current Annuity Company</Label>
                <Input
                  type="text"
                  id="annuityCompany"
                  name="annuityCompany"
                  value={formData.annuityCompany}
                  onChange={handleChange}
                  placeholder="e.g., Allianz, AIG, etc."
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="annuityType">Annuity Type</Label>
                <Select
                  id="annuityType"
                  name="annuityType"
                  value={formData.annuityType}
                  onChange={handleChange}
                >
                  <option value="">Select type...</option>
                  <option value="fixed">Fixed Annuity</option>
                  <option value="fixed-indexed">Fixed Indexed Annuity</option>
                  <option value="variable">Variable Annuity</option>
                  <option value="immediate">Immediate Annuity</option>
                  <option value="deferred">Deferred Annuity</option>
                  <option value="unsure">Not Sure</option>
                </Select>
              </FormGroup>

              <SubmitButton type="submit">
                Get My Free APE Report
              </SubmitButton>
            </Form>
          </FormContainer>
        </Container>
      </FormSection>

      <BenefitsSection>
        <Container>
          <h2 style={{ textAlign: 'center', fontSize: theme.typography.sizes['3xl'], marginBottom: theme.spacing.xl }}>
            What You'll Discover
          </h2>
          <BenefitsGrid>
            <BenefitCard>
              <BenefitIcon></BenefitIcon>
              <BenefitTitle>Real vs. Hypothetical</BenefitTitle>
              <p>Instantly see if your annuity was illustrated using real history or made-up data</p>
            </BenefitCard>
            <BenefitCard>
              <BenefitIcon></BenefitIcon>
              <BenefitTitle>Performance Tracking</BenefitTitle>
              <p>Compare your actual returns against projected performance</p>
            </BenefitCard>
            <BenefitCard>
              <BenefitIcon></BenefitIcon>
              <BenefitTitle>Better Options</BenefitTitle>
              <p>Discover high-performing indices based only on real data</p>
            </BenefitCard>
          </BenefitsGrid>
        </Container>
      </BenefitsSection>

      <CTASection>
        <Container>
          <h2 style={{ fontSize: theme.typography.sizes['2xl'], marginBottom: theme.spacing.lg }}>
            Questions? Call Us Now
          </h2>
          <PhoneNumber href="tel:800-922-1894">800-922-1894</PhoneNumber>
          <p style={{ marginTop: theme.spacing.lg, fontSize: theme.typography.sizes.lg }}>
            If your annuity's performance feels disconnected from the economy, this could be why.
          </p>
        </Container>
      </CTASection>
      </PageWrapper>
    </SharedLayout>
  );
};